/**
 * Zoom Meeting SDK Component View is a process-wide singleton (createClient once).
 * Clearing / remounting the React zoom root after leaveMeeting leaves the SDK bound
 * to a detached node — soft SPA rejoin then shows an empty room with no error.
 *
 * Keep one persistent DOM host + one client; leaveMeeting then join() again on re-enter.
 */
import { loadZoomMeetingSdkEmbedded } from './zoomMeetingSdkLoader';
import i18n from '../i18n';

const ROOT_ID = 'classroom-zoom-persistent-root';

let client = null;
let initPromise = null;
/** @type {'idle' | 'joining' | 'in-meeting' | 'left'} */
let phase = 'idle';

function zoomErrorText(err) {
  if (!err) return '';
  if (typeof err === 'string') return err;
  return String(err.reason || err.message || err.errorMessage || err.type || '');
}

async function safeLeave(c) {
  if (!c) return;
  try {
    if (typeof c.leaveMeeting === 'function') {
      await c.leaveMeeting();
      return;
    }
  } catch {
    /* fall through */
  }
  try {
    if (typeof c.leave === 'function') {
      await Promise.resolve(c.leave());
    }
  } catch {
    /* ignore */
  }
}

function ensurePersistentRoot() {
  let el = document.getElementById(ROOT_ID);
  if (!el) {
    el = document.createElement('div');
    el.id = ROOT_ID;
    el.setAttribute('data-testid', 'classroom-zoom-root');
    el.className = 'flex-1 min-h-[60vh] w-full bg-slate-900';
    el.style.display = 'none';
    document.body.appendChild(el);
  }
  return el;
}

/**
 * Attach the persistent Zoom host into the meeting page placeholder.
 * @param {HTMLElement | null} placeholder
 */
export function attachZoomClassroomRoot(placeholder) {
  const root = ensurePersistentRoot();
  root.style.display = '';
  root.className = 'flex-1 min-h-[60vh] w-full bg-slate-900';
  if (placeholder && root.parentElement !== placeholder) {
    placeholder.appendChild(root);
  }
  return root;
}

/** Hide host after leave; keep node under body so SDK init binding survives. */
export function detachZoomClassroomRoot() {
  const root = document.getElementById(ROOT_ID);
  if (!root) return;
  root.style.display = 'none';
  if (root.parentElement !== document.body) {
    document.body.appendChild(root);
  }
}

export function getZoomClassroomClient() {
  return client;
}

/**
 * Init once (if needed) and join. Safe to call after leaveMeeting on the same page session.
 * @param {object} opts
 * @param {(client: object) => (void|Promise<void>)} [opts.beforeJoin] — runs after init, before join().
 *   Attach waiting-room listeners here so host-joins-later does not miss user-added(bHold) during join.
 */
function zoomSdkLanguage() {
  const lang = String(i18n.language || 'en').split('-')[0].toLowerCase();
  switch (lang) {
    case 'de':
      return 'de-DE';
    case 'fr':
      return 'fr-FR';
    case 'nl':
      return 'nl-NL';
    default:
      return 'en-US';
  }
}

export async function joinZoomClassroom({
  containerPlaceholder,
  signature,
  sdkKey: _sdkKey,
  meetingNumber,
  password,
  userName,
  userEmail,
  zak,
  beforeJoin,
}) {
  const root = attachZoomClassroomRoot(containerPlaceholder);
  const ZoomMtgEmbedded = await loadZoomMeetingSdkEmbedded();

  if (!client) {
    client = ZoomMtgEmbedded.createClient();
  }

  if (!initPromise) {
    initPromise = client.init({
      zoomAppRoot: root,
      language: zoomSdkLanguage(),
      patchJsMedia: true,
      leaveOnPageUnload: true,
    }).catch((err) => {
      initPromise = null;
      throw err;
    });
  }
  await initPromise;

  if (phase === 'in-meeting' || phase === 'joining') {
    await safeLeave(client);
    phase = 'left';
  }

  phase = 'joining';
  if (typeof beforeJoin === 'function') {
    await beforeJoin(client);
  }

  const joinOpts = {
    signature,
    meetingNumber: String(meetingNumber),
    password: password || '',
    userName: userName || i18n.t('classroom.meeting.guest', { defaultValue: 'Guest' }),
    userEmail: userEmail || '',
  };
  if (zak) {
    joinOpts.zak = zak;
  }

  try {
    await client.join(joinOpts);
    phase = 'in-meeting';
    return client;
  } catch (err) {
    phase = 'left';
    const wrapped = new Error(zoomErrorText(err) || i18n.t('classroom.meeting.zoomJoinFailed', { defaultValue: 'Zoom join failed' }));
    wrapped.cause = err;
    throw wrapped;
  }
}

/** Leave meeting but keep client + init for a later join on this page load. */
export async function leaveZoomClassroom() {
  const c = client;
  phase = 'left';
  await safeLeave(c);
  detachZoomClassroomRoot();
}

export function zoomClassroomPhase() {
  return phase;
}
